'use client';
import { useEffect, useState, useRef, Suspense } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';

function NavigationProgressInner() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const progressRef = useRef<NodeJS.Timeout | null>(null);
  const prevPathRef = useRef(pathname + searchParams.toString());

  useEffect(() => {
    const current = pathname + searchParams.toString();
    if (current !== prevPathRef.current) {
      // Navigation completed — finish bar
      setProgress(100);
      timerRef.current = setTimeout(() => {
        setLoading(false);
        setProgress(0);
      }, 400);
      prevPathRef.current = current;
    }
  }, [pathname, searchParams]);

  // Intercept link clicks to start the loading bar
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = (e.target as HTMLElement).closest('a');
      if (!target) return;
      const href = target.getAttribute('href');
      if (!href || href.startsWith('#') || href.startsWith('http') || href.startsWith('mailto')) return;
      // Same page — skip
      const currentPath = window.location.pathname;
      if (href === currentPath) return;

      // Clear any existing timers
      if (timerRef.current) clearTimeout(timerRef.current);
      if (progressRef.current) clearInterval(progressRef.current);

      setLoading(true);
      setProgress(10);

      // Animate progress up to ~85% while waiting
      let p = 10;
      progressRef.current = setInterval(() => {
        p += Math.random() * 12;
        if (p >= 85) {
          p = 85;
          if (progressRef.current) clearInterval(progressRef.current);
        }
        setProgress(p);
      }, 200);
    };

    document.addEventListener('click', handleClick, true);
    return () => {
      document.removeEventListener('click', handleClick, true);
      if (timerRef.current) clearTimeout(timerRef.current);
      if (progressRef.current) clearInterval(progressRef.current);
    };
  }, []);

  if (!loading && progress === 0) return null;

  return (
    <div
      className="fixed top-0 left-0 right-0 z-[9999] h-[3px] pointer-events-none"
      style={{ background: 'transparent' }}
    >
      <div
        className="h-full transition-all ease-out"
        style={{
          width: `${progress}%`,
          background: 'linear-gradient(90deg, #6366f1, #8b5cf6, #a78bfa)',
          boxShadow: '0 0 8px rgba(139, 92, 246, 0.8)',
          transition: progress === 100 ? 'width 0.3s ease-out' : 'width 0.2s ease-out',
          opacity: loading || progress > 0 ? 1 : 0,
        }}
      />
    </div>
  );
}

export default function NavigationProgress() {
  return (
    <Suspense fallback={null}>
      <NavigationProgressInner />
    </Suspense>
  );
}
